import React, { useState, useEffect } from "react";
import { PoundSterling, TrendingUp, TrendingDown, Wallet, Plus, Trash2, Loader2, ArrowUpRight, ArrowDownRight, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { getFinanceSummary, getTransactions, createTransaction, deleteTransaction, getFinanceCategories } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const gbp = (n) => `\u00A3${Number(n || 0).toLocaleString()}`;

const Stat = ({ icon: Icon, label, value, tint, sub, testid }) => (
  <div className="bg-white rounded-2xl border border-[#f0ece5] p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2"><Icon size={15} style={{ color: tint }} /><span className="text-[11px] uppercase tracking-wide text-[#a0a0a0]">{label}</span></div>
    <p className="font-display text-[26px] font-500 leading-none" style={{ color: tint }}>{value}</p>
    {sub && <p className="text-[12px] text-[#8a8a8a] mt-1.5">{sub}</p>}
  </div>
);

const empty = { date: new Date().toISOString().slice(0, 10), type: "income", category: "", amount: "", description: "", party: "", method: "bank" };

const AdminFinance = () => {
  const { toast } = useToast();
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [cats, setCats] = useState({ income: [], expense: [] });
  const [busy, setBusy] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setBusy(true);
    try {
      const [s, t, c] = await Promise.all([getFinanceSummary(), getTransactions(), getFinanceCategories()]);
      setSummary(s); setTxns(t); setCats(c);
    } catch { toast({ title: "Couldn't load finances" }); }
    finally { setBusy(false); }
  };
  useEffect(() => { refresh(); }, []);

  const openNew = (type) => { setForm({ ...empty, type, category: (type === "income" ? cats.income : cats.expense)[0] || "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await createTransaction({ ...form, amount: Number(form.amount) });
      toast({ title: "Transaction added" }); setOpen(false); refresh();
    } catch (err) { toast({ title: "Save failed", description: err?.response?.data?.detail || "Check the fields." }); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm("Delete this transaction?")) return;
    try { await deleteTransaction(t.id); refresh(); } catch { toast({ title: "Delete failed" }); }
  };

  if (busy) return <div className="flex items-center justify-center py-24 text-[#8a8a8a]"><Loader2 className="animate-spin mr-2" size={18} /> Loading finances…</div>;

  const catList = form.type === "income" ? cats.income : cats.expense;
  const inputCls = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";

  return (
    <div className="space-y-6" data-testid="admin-finance">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b]">Finances</h3>
        <div className="flex gap-2">
          <button onClick={() => openNew("income")} data-testid="add-income-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-green-600 text-white text-[13px] font-500 hover:bg-green-700 transition-colors"><ArrowUpRight size={15} /> Income</button>
          <button onClick={() => openNew("expense")} data-testid="add-expense-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1c1c1e] text-white text-[13px] font-500 hover:bg-black transition-colors"><ArrowDownRight size={15} /> Expense</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={TrendingUp} label="Income" value={gbp(summary.income)} tint="#16a34a" sub="all recorded" testid="stat-income" />
        <Stat icon={TrendingDown} label="Expenses" value={gbp(summary.expenses)} tint="#dc2626" sub="all recorded" testid="stat-expenses" />
        <Stat icon={Wallet} label="Net profit" value={gbp(summary.net)} tint={summary.net >= 0 ? "#16a34a" : "#dc2626"} sub="income − expenses" testid="stat-net" />
        <Stat icon={FileText} label="Outstanding" value={gbp(summary.outstandingInvoices)} tint="#b8935f" sub="unpaid invoices" testid="stat-outstanding" />
      </div>

      <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
        <h4 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-4">Cashflow — last 6 months</h4>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={summary.cashflow} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0ece5" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#a0a0a0" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#a0a0a0" }} axisLine={false} tickLine={false} width={52} tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => gbp(v)} contentStyle={{ borderRadius: 8, border: "1px solid #f0ece5", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="income" name="Income" fill="#16a34a" radius={[4, 4, 0, 0]} />
            <Bar dataKey="expense" name="Expense" fill="#1c1c1e" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
          <h4 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-3">Income by category</h4>
          {summary.incomeByCategory.length === 0 ? <p className="text-[13px] text-[#a0a0a0]">No income yet.</p> :
            summary.incomeByCategory.map((c) => (
              <div key={c.category} className="flex items-center justify-between py-1.5 border-b border-[#faf7f2] last:border-0">
                <span className="text-[13px] text-[#4a4a4a]">{c.category}</span>
                <span className="text-[13px] font-500 text-green-700">{gbp(c.amount)}</span>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
          <h4 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-3">Expenses by category</h4>
          {summary.expenseByCategory.length === 0 ? <p className="text-[13px] text-[#a0a0a0]">No expenses yet.</p> :
            summary.expenseByCategory.map((c) => (
              <div key={c.category} className="flex items-center justify-between py-1.5 border-b border-[#faf7f2] last:border-0">
                <span className="text-[13px] text-[#4a4a4a]">{c.category}</span>
                <span className="text-[13px] font-500 text-[#1c1c1e]">{gbp(c.amount)}</span>
              </div>
            ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
        <h4 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-3">Recent transactions ({txns.length})</h4>
        <div className="space-y-1.5" data-testid="txn-list">
          {txns.slice(0, 40).map((t) => (
            <div key={t.id} className="flex items-center justify-between p-2.5 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
              <div className="flex items-center gap-3">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.type === "income" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                  {t.type === "income" ? <ArrowUpRight size={15} /> : <ArrowDownRight size={15} />}
                </span>
                <div>
                  <p className="text-[13px] font-500 text-[#2b2b2b]">{t.category}</p>
                  <p className="text-[11px] text-[#a0a0a0]">{t.date} · {t.party || t.description || t.method}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[14px] font-500 ${t.type === "income" ? "text-green-700" : "text-[#1c1c1e]"}`}>{t.type === "income" ? "+" : "−"}{gbp(t.amount)}</span>
                <button onClick={() => remove(t)} className="w-8 h-8 rounded-lg border border-[#e2d8c8] flex items-center justify-center text-red-500 hover:border-red-400 transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogTitle className="sr-only">Add transaction</DialogTitle>
          <h2 className="font-display text-[22px] font-300 text-[#2b2b2b] mb-4">Add {form.type}</h2>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="txn-date" />
              <input type="number" step="0.01" required placeholder="Amount (£)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} data-testid="txn-amount" />
            </div>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="h-[44px] border-[#e2d8c8]" data-testid="txn-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>{catList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <input placeholder="From / to (party)" value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} className={inputCls} data-testid="txn-party" />
            <input placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger className="h-[44px] border-[#e2d8c8]"><SelectValue /></SelectTrigger>
              <SelectContent>{["bank", "card", "cash", "transfer"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <button type="submit" disabled={saving} data-testid="txn-save" className="w-full h-[48px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">{saving ? "Saving…" : `Add ${form.type}`}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFinance;
